import React from 'react';
import SummaryTabBase from '../Shared/SummaryTabBase';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';

interface Props {
  landUseChangeTotal: number | null;
  buildingConstructionTotal: number | null;
  roadPavementTotal: number | null;
  energyConsumptionConstructionTotal: number | null;
  construnctionTotal: number | null;
  fuelTotal: number | null;
  vehicleTotal: number | null;
  co2: number | null;
  ch4: number | null;
  n2o: number | null;
  rawMaterialUseTotal: number | null;
  energyConsumptionOperationTotal: number | null;
  streetlightsTotal: number | null;
  roadMaintenanceTotal: number | null;
  energyEfficiencyTotal: number | null;
  totalConstructionOverriden: number | null;
  setTotalConstructionOverriden: React.Dispatch<React.SetStateAction<number | null>>;
  totalOperationOverriden: number | null;
  setTotalOperationOverriden: React.Dispatch<React.SetStateAction<number | null>>;
}

const calculateTotal = (...subtotals: (number | null)[]) => {
  if (subtotals.every((subtotal) => subtotal === null)) {
    return null;
  }

  return subtotals.reduce((acc: number, subtotal) => acc + (subtotal || 0), 0);
};

const calculateNormalized = (construction: number | null, operation: number | null) => {
  const constructionValue = construction !== null ? construction : 0;
  const operationValue = operation !== null ? operation : 0;

  const subTotal = constructionValue + operationValue * 20;
  return subTotal > 0 ? subTotal / 20 : 0;
};

const SummaryTab: React.FC<Props> = ({
  landUseChangeTotal,
  buildingConstructionTotal,
  roadPavementTotal,
  energyConsumptionConstructionTotal,
  construnctionTotal,
  fuelTotal,
  vehicleTotal,
  co2,
  ch4,
  n2o,
  rawMaterialUseTotal,
  energyConsumptionOperationTotal,
  streetlightsTotal,
  roadMaintenanceTotal,
  energyEfficiencyTotal,
  totalConstructionOverriden,
  setTotalConstructionOverriden,
  totalOperationOverriden,
  setTotalOperationOverriden,
}) => {
  const anyConstruction = !!(
    landUseChangeTotal ||
    buildingConstructionTotal ||
    roadPavementTotal ||
    construnctionTotal ||
    rawMaterialUseTotal ||
    energyConsumptionConstructionTotal
  );

  const totalSubTotalConstruction = calculateTotal(
    landUseChangeTotal,
    buildingConstructionTotal,
    roadPavementTotal,
    energyConsumptionConstructionTotal,
    construnctionTotal,
    rawMaterialUseTotal
  );

  const anyOperations = !!(
    streetlightsTotal ||
    roadMaintenanceTotal ||
    energyConsumptionOperationTotal
  );

  const totalSubTotalOperation = calculateTotal(
    streetlightsTotal,
    roadMaintenanceTotal,
    energyConsumptionOperationTotal
  );

  const constructionTableConfig = {
    colgroup: (
      <colgroup>
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '12%' }} />
      </colgroup>
    ),
    headers: (
      <tr>
        <th>Land Use Change</th>
        <th>Build Construction</th>
        <th>Energy Consumption</th>
        <th>Construction Equipment</th>
        <th>Fuel Consumption</th>
        <th>Vehicle Distance Traveled</th>
        <th>
          CO<sub>2</sub>
        </th>
        <th>CH4</th>
        <th>N20</th>
        <th>Road Pavement</th>
        <th>Raw Material Use</th>
        <th>Subtotal</th>
      </tr>
    ),
    row: (
      <tr>
        <td>{landUseChangeTotal && <NumberFormatterSinSeparador value={landUseChangeTotal} />}</td>
        <td>
          {buildingConstructionTotal && (
            <NumberFormatterSinSeparador value={buildingConstructionTotal} />
          )}
        </td>
        <td>
          {energyConsumptionConstructionTotal && (
            <NumberFormatterSinSeparador value={energyConsumptionConstructionTotal} />
          )}
        </td>
        <td>{construnctionTotal && <NumberFormatterSinSeparador value={construnctionTotal} />}</td>
        <td>{fuelTotal && <NumberFormatterSinSeparador value={fuelTotal} />}</td>
        <td>{vehicleTotal && <NumberFormatterSinSeparador value={vehicleTotal} />}</td>
        <td>{co2 && <NumberFormatterSinSeparador value={co2} />}</td>
        <td>{ch4 && <NumberFormatterSinSeparador value={ch4} />}</td>
        <td>{n2o && <NumberFormatterSinSeparador value={n2o} />}</td>
        <td>{roadPavementTotal && <NumberFormatterSinSeparador value={roadPavementTotal} />}</td>
        <td>
          {rawMaterialUseTotal && <NumberFormatterSinSeparador value={rawMaterialUseTotal} />}
        </td>
        <td className="summary-subtotal">
          {anyConstruction && totalSubTotalConstruction && (
            <>
              <NumberFormatterSinSeparador value={totalSubTotalConstruction} /> t CO<sub>2</sub>e
            </>
          )}
        </td>
      </tr>
    ),
  };

  const operationTableConfig = {
    colgroup: (
      <colgroup>
        <col style={{ width: '25%' }} />
        <col style={{ width: '31%' }} />
        <col style={{ width: '32%' }} />
        <col style={{ width: '12%' }} />
      </colgroup>
    ),
    headers: (
      <tr>
        <th>Method 1: Energy Consumption</th>
        <th>Method 2: Streetlights only</th>
        <th>Road Maintenance</th>
        <th>Subtotal</th>
      </tr>
    ),
    row: (
      <tr>
        <td>
          {energyConsumptionOperationTotal && (
            <>
              <NumberFormatterSinSeparador value={energyConsumptionOperationTotal} /> t CO
              <sub>2</sub>e
            </>
          )}
        </td>
        <td>
          {streetlightsTotal && (
            <>
              <NumberFormatterSinSeparador value={streetlightsTotal} /> t CO<sub>2</sub>e
            </>
          )}
        </td>
        <td>
          {roadMaintenanceTotal && (
            <>
              <NumberFormatterSinSeparador value={roadMaintenanceTotal} /> t CO<sub>2</sub>e
            </>
          )}
        </td>
        <td className="summary-subtotal">
          {anyOperations && (
            <>
              <NumberFormatterSinSeparador value={totalSubTotalOperation} /> t CO<sub>2</sub>e
            </>
          )}
        </td>
      </tr>
    ),
  };

  return (
    <SummaryTabBase
      totalConstructionOverriden={totalConstructionOverriden}
      setTotalConstructionOverriden={setTotalConstructionOverriden}
      totalOperationOverriden={totalOperationOverriden}
      setTotalOperationOverriden={setTotalOperationOverriden}
      constructionTableConfig={constructionTableConfig}
      operationTableConfig={operationTableConfig}
      totalSubTotalConstruction={totalSubTotalConstruction}
      totalSubTotalOperation={totalSubTotalOperation}
      anyConstruction={anyConstruction}
      anyOperations={anyOperations}
      calculateNormalized={calculateNormalized}
      showNormalizedColumn
      energyEfficiencyTotal={energyEfficiencyTotal}
    />
  );
};

export default SummaryTab;
